import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  FireOutlined,
  LayoutOutlined,
  LockOutlined,
  OrderedListOutlined,
  PlayCircleFilled,
  RightOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  StarFilled,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, App, Empty, Progress, Skeleton, Tag as AntTag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { educationApi } from '../lib/wikiApi';
import { learnApi, type LearnCourseDetail, type LearnCourseListItem } from '../lib/api/learn';
import { authApi } from '../lib/authApi';
import { useRoleLabels } from '../lib/useRoleLabels';
import { formatMinutes, lessonMeta } from '../lib/lesson-meta';
import { Button, Chip, PageTitle, Ring, Tabs } from '../components/ds';
import { CourseCover, CoverArt, CoverHeroText, coverVisual } from '../components/learn/CourseCover';
import { pathDays, pluralRu } from '../components/learn/learn-utils';
import './Learn.css';

/** Тест урока — экран по одному вопросу (components/learn/QuizFlow.tsx). */
export { QuizPage } from '../components/learn/QuizFlow';

/**
 * Обучение (HUB 2.0, PageLearn → «Обучение»): ученику — «Продолжить»
 * (курс и урок, с которого продолжать) и карточка траектории; вкладки
 * Мои курсы / Обязательные / Все / Пройденные (?tab= в URL); УК — чипы
 * ролей и кнопка конструктора. Карточка: обложка, «Обязательный», уровень,
 * сертификат, уроки · минуты · срок, прогресс.
 *
 * Маршрут: /education
 */
type EduTab = 'my' | 'required' | 'all' | 'done';
const TAB_KEYS: EduTab[] = ['my', 'required', 'all', 'done'];

function fmtDay(iso: string | null | undefined, month: 'short' | 'long' = 'long'): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month }) : '';
}

function daysLeft(iso: string | null): number | null {
  return iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000) : null;
}

/** «срок до 9 октября» / «просрочен 2 дн.» / «осталось 3 дн.» + тон. */
function dueInfo(dueAt: string | null, done: boolean): { text: string; tone: 'muted' | 'warn' | 'bad' } | null {
  if (!dueAt || done) return null;
  const d = daysLeft(dueAt)!;
  if (d < 0) return { text: `просрочен ${-d} дн.`, tone: 'bad' };
  if (d <= 3) return { text: d === 0 ? 'срок сегодня' : `осталось ${d} дн.`, tone: 'warn' };
  return { text: `до ${fmtDay(dueAt)}`, tone: 'muted' };
}

const TONE_COLOR = { muted: 'var(--fk-text-muted)', warn: 'var(--fk-warning-text, var(--fk-warning))', bad: 'var(--fk-error)' } as const;

export function EducationPage(): JSX.Element {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const me = useQuery({ queryKey: ['wiki', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'education', 'courses'],
    queryFn: () => learnApi.listCourses(),
  });
  const paths = useQuery({ queryKey: ['wiki', 'education', 'paths'], queryFn: () => educationApi.myPaths(), retry: false });
  const { label: roleLabel } = useRoleLabels();
  // Очки и серия в шапке (PageMobile: «как в игре») — тот же кеш, что виджет на главной.
  const gami = useQuery({ queryKey: ['wiki', 'gamification', 'mine'], queryFn: () => educationApi.gamificationMine(), staleTime: 60_000, retry: false });

  const courses = useMemo(() => data ?? [], [data]);
  const isUK = !!me.data?.canAdmin;
  const mine = useMemo(() => {
    const list = courses.filter((c) => !c.isCompleted && (c.isAssigned || c.progressPercent > 0 || c.isRequired));
    return [...list].sort((a, b) => {
      const ta = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
      const tb = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
      return ta - tb || b.progressPercent - a.progressPercent;
    });
  }, [courses]);
  const required = useMemo(() => courses.filter((c) => c.isRequired), [courses]);
  const done = useMemo(() => courses.filter((c) => c.isCompleted), [courses]);

  // Вкладка по умолчанию: есть свои курсы — «Мои», иначе «Все».
  const tabParam = sp.get('tab') as EduTab | null;
  const tab: EduTab = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : mine.length > 0 ? 'my' : 'all';
  const role = sp.get('role') ?? 'all';
  const setParam = (k: string, v: string | null): void => {
    const next = new URLSearchParams(sp);
    if (v == null) next.delete(k);
    else next.set(k, v);
    setSp(next, { replace: true });
  };

  const roleChips = useMemo(() => {
    const set = new Set<string>();
    courses.forEach((c) => c.forRoles.forEach((r) => set.add(r)));
    return Array.from(set).sort((a, b) => roleLabel(a).localeCompare(roleLabel(b), 'ru'));
  }, [courses, roleLabel]);

  const list = useMemo(() => {
    const base = tab === 'my' ? mine : tab === 'required' ? required : tab === 'done' ? done : courses;
    return role === 'all' ? base : base.filter((c) => c.forRoles.includes(role));
  }, [tab, role, mine, required, done, courses]);

  // «Продолжить»: начатый курс с ближайшим сроком, иначе назначенный/обязательный.
  const cont = useMemo(() => mine.find((c) => c.progressPercent > 0 && c.continueLessonId) ?? mine.find((c) => c.continueLessonId) ?? null, [mine]);
  const activePath = (paths.data ?? []).find((p) => !p.completedAt) ?? null;

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) return <Alert type="error" showIcon message="Не удалось загрузить курсы" description={(error as Error).message} />;

  const certCount = done.filter((c) => c.hasCertificate).length;
  const sub = isUK
    ? `${courses.length} ${pluralRu(courses.length, 'курс', 'курса', 'курсов')} на витрине · ${required.length} обязательных`
    : [
        mine.length ? `${mine.length} в работе` : null,
        done.length ? `${done.length} пройдено` : null,
        certCount ? `${certCount} ${pluralRu(certCount, 'сертификат', 'сертификата', 'сертификатов')}` : null,
      ].filter(Boolean).join(' · ') || 'Курсы для вашей роли: микроуроки, тесты, практика';

  return (
    <div>
      <PageTitle
        title="Обучение"
        sub={sub}
        right={
          (isUK || me.data?.canEdit) ? (
            <Button variant="ghost" icon={<LayoutOutlined />} to="/admin/courses">Открыть конструктор курсов</Button>
          ) : gami.data ? (
            <Link to="/points" className="fk-learn-card" title="Мои очки и бейджи" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 12px', fontWeight: 700, fontSize: 13, color: 'var(--fk-text)', minHeight: 40, boxShadow: 'none' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FireOutlined style={{ color: 'var(--fk-orange)' }} />{gami.data.streakDays}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><StarFilled style={{ color: 'var(--fk-orange)' }} />{gami.data.points.toLocaleString('ru-RU')}</span>
            </Link>
          ) : undefined
        }
      />

      {(cont || activePath) && (
        <div className="fk-learn-top">
          {cont ? <ContinueHero c={cont} onGo={() => navigate(`/lesson/${cont.continueLessonId}`)} /> : <div />}
          {activePath ? (
            <PathMiniCard p={activePath} />
          ) : (
            <div className="fk-learn-card fk-learn-pathcard">
              <Ring value={courses.length ? Math.round((done.length / courses.length) * 100) : 0} size={64} stroke={6} doneColor="var(--fk-success)" />
              <div style={{ minWidth: 0 }}>
                <div className="fk-learn-eyebrow">Мой прогресс</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>{done.length} из {courses.length} курсов пройдено</div>
                <Link to="/points" style={{ fontSize: 13, fontWeight: 600 }}>Мои очки и бейджи →</Link>
              </div>
            </div>
          )}
        </div>
      )}

      {courses.length === 0 ? (
        <Empty description="Курсы скоро появятся" style={{ marginTop: 40 }} />
      ) : (
        <>
          <div className="fk-learn-toolbar">
            <Tabs<EduTab>
              value={tab}
              onChange={(k) => setParam('tab', k)}
              items={[
                { key: 'my', label: 'Мои курсы', badge: mine.length || null, badgeColor: 'var(--fk-orange)' },
                { key: 'required', label: 'Обязательные', badge: required.filter((c) => !c.isCompleted).length || null },
                { key: 'all', label: 'Все' },
                { key: 'done', label: 'Пройденные', badge: done.length || null, badgeColor: 'var(--fk-success)' },
              ]}
            />
            {isUK && roleChips.length > 1 && (
              <div className="fk-learn-chips">
                <Chip active={role === 'all'} onClick={() => setParam('role', null)}>Все роли</Chip>
                {roleChips.map((r) => (
                  <Chip key={r} active={role === r} onClick={() => setParam('role', r)}>{roleLabel(r)}</Chip>
                ))}
              </div>
            )}
          </div>
          {list.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={tab === 'my' ? 'Назначенных курсов нет — загляните во «Все»' : tab === 'done' ? 'Пока нет пройденных курсов' : 'Курсов нет'}
              style={{ margin: '32px 0' }}
            />
          ) : (
            <div className="fk-learn-grid">
              {list.map((c) => <CourseCard key={c.id} c={c} />)}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <MySkillsBlock />
      </div>
    </div>
  );
}

/** Тёмный герой «Продолжить»: курс, урок N из M, минуты, срок, прогресс. */
function ContinueHero({ c, onGo }: { c: LearnCourseListItem; onGo: () => void }): JSX.Element {
  const due = dueInfo(c.dueAt, c.isCompleted);
  const meta = [
    c.continueLessonIndex && c.totalLessons ? `Урок ${c.continueLessonIndex} из ${c.totalLessons}` : null,
    c.continueLessonTitle ? `«${c.continueLessonTitle}»` : null,
    c.continueLessonMinutes ? formatMinutes(c.continueLessonMinutes) : null,
    due ? (due.tone === 'muted' ? `срок ${due.text}` : due.text) : null,
  ].filter(Boolean).join(' · ');
  const v = coverVisual(c);
  return (
    <section className="fk-learn-dark fk-learn-hero" style={{ background: v.heroBg }}>
      <CoverArt v={v} />
      <CoverHeroText v={v} />
      <div className="fk-learn-eyebrow">{c.progressPercent > 0 ? 'Продолжить' : 'Начать'}</div>
      <div>
        <div className="fk-learn-hero-title">{c.name}</div>
        {meta && <div className="fk-learn-hero-meta">{meta}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, maxWidth: 440 }}>
        <div className="fk-learn-dark-bar"><span style={{ width: `${c.progressPercent}%` }} /></div>
        <span className="fk-learn-mono" style={{ fontSize: 13, fontWeight: 700 }}>{c.progressPercent}%</span>
      </div>
      <div>
        <Button variant="primary" size="lg" icon={<PlayCircleFilled />} onClick={onGo}>
          {c.progressPercent > 0 ? 'Продолжить урок' : 'Начать курс'}
        </Button>
      </div>
    </section>
  );
}

/** Карточка активной траектории: кольцо, день X из N, шаги, следующий срок. */
function PathMiniCard({ p }: { p: NonNullable<Awaited<ReturnType<typeof educationApi.myPaths>>>[number] }): JSX.Element {
  const pct = p.totalSteps === 0 ? 0 : Math.round((p.doneSteps / p.totalSteps) * 100);
  const days = pathDays(p.assignedAt, p.dueAt, p.steps.map((s) => s.dueAt));
  const nextDue = p.steps.filter((s) => !s.done && s.dueAt).map((s) => s.dueAt as string).sort()[0] ?? null;
  return (
    <section className="fk-learn-card fk-learn-pathcard">
      <Ring value={pct} size={72} stroke={7} doneColor="var(--fk-success)" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="fk-learn-eyebrow">Траектория</div>
        <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>{p.name}</div>
        <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 2 }}>
          День {days.day} из {days.total} · {p.doneSteps} из {p.totalSteps} шагов{nextDue ? ` · следующий срок — ${fmtDay(nextDue)}` : ''}
        </div>
        <div className="fk-learn-days">{days.cells.map((d, i) => <span key={i} data-d={d} />)}</div>
        <Link to={`/path/${p.assignmentId}`} style={{ fontSize: 13, fontWeight: 600, display: 'inline-block', marginTop: 8 }}>Открыть траекторию →</Link>
      </div>
    </section>
  );
}

/** Карточка курса каталога (PageLearn): обложка, бейджи, мета, прогресс. */
function CourseCard({ c }: { c: LearnCourseListItem }): JSX.Element {
  const due = dueInfo(c.dueAt, c.isCompleted);
  const lessons = c.totalLessons ?? c.totalModules;
  return (
    <Link to={`/course/${c.id}`} className="fk-learn-card fk-learn-ccard">
      <CourseCover cover={c}>
        {c.isCompleted ? (
          <span className="fk-learn-badge" style={{ background: 'var(--fk-success)', color: '#fff' }}><CheckOutlined /> Пройден</span>
        ) : c.isRequired ? (
          <span className="fk-learn-badge" style={{ background: 'rgba(255,255,255,.92)', color: '#1A1F36' }}>Обязательный</span>
        ) : null}
        {c.level && <span className="fk-learn-badge" style={{ background: 'rgba(37,29,74,.55)', color: '#fff' }}>{c.level}</span>}
      </CourseCover>
      <div className="fk-learn-cbody">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div className="fk-learn-cname">{c.name}</div>
          {c.hasCertificate && <SafetyCertificateOutlined title="Выдаёт сертификат" style={{ color: 'var(--fk-orange)', fontSize: 16, marginTop: 2 }} />}
        </div>
        {c.description && <div className="fk-learn-cdesc">{c.description}</div>}
        <div className="fk-learn-cmeta">
          <span>{lessons} {pluralRu(lessons, 'урок', 'урока', 'уроков')}</span>
          {c.estimatedMinutes ? <><span>·</span><span>{formatMinutes(c.estimatedMinutes)}</span></> : c.durationLabel ? <><span>·</span><span>{c.durationLabel}</span></> : null}
          {due && <><span>·</span><span style={{ color: TONE_COLOR[due.tone], fontWeight: due.tone === 'muted' ? 400 : 700 }}>{due.text}</span></>}
        </div>
        <div className="fk-learn-cprog">
          <div className="fk-learn-bar" data-done={c.isCompleted}><span style={{ width: `${c.progressPercent}%` }} /></div>
          <b>{c.progressPercent}%</b>
        </div>
      </div>
    </Link>
  );
}

// ─── CoursePage — страница курса ───────────────────────────────────

export function CoursePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'education', 'course', id],
    queryFn: () => learnApi.getCourse(id!),
    enabled: !!id,
    retry: false,
  });

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) {
    const status = (error as { status?: number }).status;
    if (status === 403) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Курс не для вашей роли"
          description="Этот курс назначен другим ролям. Вернитесь на страницу обучения."
          action={<Link to="/education">К списку</Link>}
        />
      );
    }
    return <Alert type="error" showIcon message="Не удалось загрузить курс" description={(error as Error).message} action={<Link to="/education">К обучению</Link>} />;
  }
  if (!data) return <Empty description="Курс не найден" />;
  return <CourseDetailView course={data} />;
}

function CourseDetailView({ course }: { course: LearnCourseDetail }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['wiki', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const { text: rolesText } = useRoleLabels();
  // Фаза 2.7: подтягиваем первые непройденные уроки заранее — SW положит
  // ответы в кеш, и урок откроется в зале без сети.
  useEffect(() => {
    const ids = course.modules.flatMap((m) => m.lessons).filter((l) => !l.isCompleted).slice(0, 5).map((l) => l.id);
    for (const lid of ids) {
      void qc.prefetchQuery({ queryKey: ['wiki', 'lesson', lid], queryFn: () => learnApi.getLesson(lid), staleTime: 60_000 });
    }
  }, [course.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allLessons = course.modules.flatMap((m) => m.lessons);
  const hasLessons = allLessons.length > 0;
  const started = course.completedLessons > 0;
  const cta = course.isCompleted ? 'Повторить курс' : started ? 'Продолжить' : 'Начать курс';
  const due = dueInfo(course.dueAt, course.isCompleted);
  const recert = course.recertifyEveryDays
    ? ` · переаттестация раз в ${course.recertifyEveryDays >= 60 ? `${Math.round(course.recertifyEveryDays / 30)} мес.` : `${course.recertifyEveryDays} дн.`}`
    : '';
  const required = course.requiredLessons ?? allLessons.length;
  const cv = coverVisual(course);
  const progressText = hasLessons
    ? `${course.completedLessons} из ${allLessons.length} уроков${required < allLessons.length ? ` · обязательных ${required}` : ''}`
    : `${course.completedModules} из ${course.totalModules} модулей`;

  return (
    <div>
      <div className="fk-learn-chero" style={{ background: cv.heroBg }}>
        <CoverArt v={cv} />
        <CoverHeroText v={cv} />
        <Link to="/education" className="fk-learn-back"><ArrowLeftOutlined /> Обучение</Link>
        <h1>{course.name}</h1>
        {course.description && <div className="fk-learn-chero-desc">{course.description}</div>}
        <div className="fk-learn-chero-meta">
          <span><OrderedListOutlined /> {hasLessons ? `${allLessons.length} ${pluralRu(allLessons.length, 'урок', 'урока', 'уроков')}` : `${course.totalModules} модулей`} · {course.totalModules} {pluralRu(course.totalModules, 'раздел', 'раздела', 'разделов')}</span>
          {course.estimatedMinutes ? <span><ClockCircleOutlined /> {formatMinutes(course.estimatedMinutes)}</span> : null}
          {course.level && <span><RiseOutlined /> {course.level}</span>}
          {course.hasCertificate && <span><SafetyCertificateOutlined /> Сертификат{recert}</span>}
          {course.authorName && <span><UserOutlined /> {course.authorName}</span>}
        </div>
      </div>

      <div className="fk-learn-cgrid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          {course.objectives.length > 0 && (
            <section className="fk-learn-card fk-learn-pad">
              <h2 className="fk-learn-h2">Чему научитесь</h2>
              <div className="fk-learn-objectives">
                {course.objectives.map((o, i) => (
                  <div key={i}><CheckCircleFilled style={{ color: 'var(--fk-success)', marginTop: 3 }} /><span>{o}</span></div>
                ))}
              </div>
            </section>
          )}

          <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h2 className="fk-learn-h2" style={{ margin: 0 }}>Программа курса</h2>
            {course.modules.map((m, idx) => (
              <ModuleBlock key={m.id} m={m} idx={idx} courseId={course.id} continueId={course.isCompleted ? null : course.continueLessonId} />
            ))}
          </section>

          <TeamProgressSection courseId={course.id} />
        </div>

        <aside className="fk-learn-side">
          <section className="fk-learn-card fk-learn-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Ring value={course.progressPercent} size={72} stroke={7} doneColor="var(--fk-success)" />
              <div>
                <div className="fk-learn-eyebrow">Ваш прогресс</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{course.progressPercent}%</div>
                <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)' }}>{progressText}</div>
              </div>
            </div>
            {course.continueLessonId && (
              <Button variant="primary" size="lg" block icon={<PlayCircleFilled />} onClick={() => navigate(`/lesson/${course.continueLessonId}`)}>
                {cta}
              </Button>
            )}
            {due && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: TONE_COLOR[due.tone], fontWeight: due.tone === 'muted' ? 500 : 700 }}>
                <CalendarOutlined /> Срок прохождения: {fmtDay(course.dueAt)}{due.tone !== 'muted' ? ` · ${due.text}` : ''}
              </div>
            )}
          </section>

          {course.hasCertificate && <CertificateCard course={course} />}

          <section className="fk-learn-card fk-learn-pad">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>О курсе</div>
            <div className="fk-learn-kv"><span>Для ролей</span><b>{rolesText(course.forRoles, 'все роли')}</b></div>
            {course.passingScore != null && <div className="fk-learn-kv"><span>Проходной балл</span><b>{course.passingScore} %</b></div>}
            {course.learnersCount != null && <div className="fk-learn-kv"><span>Учеников</span><b>{course.learnersCount}</b></div>}
            {course.updatedAt && <div className="fk-learn-kv"><span>Обновлён</span><b>{new Date(course.updatedAt).toLocaleDateString('ru-RU')}</b></div>}
            {course.isRequired && <div className="fk-learn-kv"><span>Обязательный</span><b>да</b></div>}
            {me.data?.canAdmin && (
              <Link to={`/admin/courses?course=${encodeURIComponent(course.id)}`} style={{ display: 'inline-block', marginTop: 10, fontSize: 13, fontWeight: 600 }}>
                Редактировать в конструкторе →
              </Link>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

/** Раздел программы: номер/галочка, мета, уроки (тип, минуты, балл, «Вы здесь», замок). */
function ModuleBlock({ m, idx, courseId, continueId }: { m: LearnCourseDetail['modules'][number]; idx: number; courseId: string; continueId: string | null }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  // Legacy-модуль без уроков и без теста — «прочитал» (как раньше).
  const markRead = useMutation({
    mutationFn: () => educationApi.markModuleRead(m.id),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'education'] });
      message.success(r.courseCompleted ? '🎉 Курс пройден!' : 'Модуль завершён');
    },
    onError: (e: Error) => message.error(e.message),
  });
  const done = m.lessons.filter((l) => l.isCompleted).length;
  const minutes = m.lessons.reduce((a, l) => a + (l.estimatedMinutes ?? 0), 0);
  const hasCurrent = m.lessons.some((l) => l.id === continueId);
  // Legacy-модуль без уроков: статья + тест модуля.
  if (m.lessons.length === 0) {
    return (
      <div className="fk-learn-card fk-learn-module">
        <div className="fk-learn-module-head">
          <span className="fk-learn-num" data-done={m.isCompleted}>{m.isCompleted ? <CheckOutlined /> : idx + 1}</span>
          <div style={{ flex: 1, fontWeight: 700 }}>{m.title}</div>
          {m.isLocked && <LockOutlined style={{ color: 'var(--fk-text-muted)' }} />}
        </div>
        <div style={{ padding: '12px 18px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {m.article && <Button variant="ghost" size="sm" to={`/article/${m.article.slug}`}>Статья: {m.article.title}</Button>}
          {m.quiz && !m.isCompleted && <Button variant="primary" size="sm" onClick={() => navigate(`/course/${courseId}/quiz/${m.quiz!.id}`)}>Пройти тест</Button>}
          {m.hasTrainer && <Button variant="soft" size="sm" to={`/lesson/${m.id}`}>Открыть тренажёр</Button>}
          {m.videoUrl && <Button variant="ghost" size="sm" href={m.videoUrl} icon={<PlayCircleFilled />}>Смотреть видео</Button>}
          {!m.quiz && !m.hasTrainer && !m.isCompleted && !m.isLocked && (
            <Button variant="primary" size="sm" icon={<CheckOutlined />} loading={markRead.isPending} onClick={() => markRead.mutate()}>Отметить как изученное</Button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="fk-learn-card fk-learn-module">
      <div className="fk-learn-module-head">
        <span className="fk-learn-num" data-done={m.isCompleted} data-current={!m.isCompleted && hasCurrent}>{m.isCompleted ? <CheckOutlined /> : idx + 1}</span>
        <div style={{ flex: 1, fontWeight: 700, minWidth: 0 }}>{m.title}</div>
        <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', whiteSpace: 'nowrap' }}>
          {done}/{m.lessons.length}{minutes ? ` · ${formatMinutes(minutes)}` : ''}
        </span>
        {m.isLocked && <LockOutlined title="Откроется после предыдущих разделов" style={{ color: 'var(--fk-text-muted)' }} />}
      </div>
      {m.lessons.map((l) => {
        const lm = lessonMeta(l.type);
        const current = l.id === continueId;
        const sub = [
          lm.label,
          l.estimatedMinutes ? formatMinutes(l.estimatedMinutes) : null,
          l.bestScore != null ? `балл ${l.bestScore}%${l.passingScore != null && l.bestScore >= l.passingScore ? ' ✓' : ''}` : null,
          l.isOptional ? 'необязательный' : null,
        ].filter(Boolean).join(' · ');
        return (
          <Link
            key={l.id}
            to={`/lesson/${l.id}`}
            className="fk-learn-lrow"
            data-current={current}
            data-locked={!!l.isLocked && !l.isCompleted}
            title={l.lockHard ? 'Откроется после предыдущего урока' : undefined}
          >
            <span className="fk-learn-licon" style={{ color: lm.color, background: `color-mix(in srgb, ${lm.color} 12%, transparent)` }}>{lm.icon}</span>
            <span style={{ minWidth: 0 }}>
              <span className="fk-learn-ltitle" style={{ display: 'block' }}>{l.title}</span>
              <span className="fk-learn-lsub">{sub}</span>
            </span>
            <span>{current ? <span className="fk-learn-here">Вы здесь</span> : null}</span>
            <span style={{ fontSize: 15 }}>
              {l.isCompleted ? (
                <CheckCircleFilled style={{ color: 'var(--fk-success)' }} />
              ) : l.isLocked ? (
                <LockOutlined style={{ color: 'var(--fk-text-muted)' }} />
              ) : (
                <RightOutlined style={{ fontSize: 11, color: 'var(--fk-text-muted)' }} />
              )}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * «Мой сертификат» (кит «LMS»): курс пройден — номер, дата, срок действия и
 * PDF (ленивая выдача на бэке); иначе — сколько осталось до сертификата.
 * Получают и CRM-пользователи, и сотрудники iiko (ФИО из iiko).
 */
function CertificateCard({ course }: { course: LearnCourseDetail }): JSX.Element {
  const certQuery = useQuery({
    queryKey: ['wiki', 'education', 'certificate', course.id],
    queryFn: () => educationApi.getCertificate(course.id),
    enabled: course.isCompleted,
    retry: false,
    staleTime: 60_000,
  });
  const cert = certQuery.data;
  return (
    <section className="fk-learn-dark" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,105,0,.18)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <TrophyOutlined style={{ fontSize: 22, color: 'var(--fk-orange)' }} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>Сертификат</div>
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.72)', lineHeight: 1.45 }}>
            {!course.isCompleted
              ? `На ${course.progressPercent}% к получению — пройдите курс до конца`
              : certQuery.isLoading
                ? 'Готовим сертификат…'
                : cert
                  ? <>№ {cert.number} · {new Date(cert.issuedAt).toLocaleDateString('ru-RU')}{cert.expiresAt ? <><br />{cert.isExpired ? '⚠ Срок истёк — пройдите курс заново' : `Действует до ${new Date(cert.expiresAt).toLocaleDateString('ru-RU')}`}</> : null}</>
                  : ((certQuery.error as Error | null)?.message ?? 'Сертификат пока недоступен')}
          </div>
        </div>
      </div>
      {course.isCompleted && cert && (
        <Button variant="indigo" href={educationApi.certificatePdfUrl(course.id)} icon={<SafetyCertificateOutlined />}>Открыть сертификат (PDF)</Button>
      )}
    </section>
  );
}

/**
 * Прогресс команды (Q61): УК — все, менеджер партнёра — своя команда,
 * управляющий iiko — своя точка. Остальным бэк отдаёт 403 — секция скрыта.
 */
function TeamProgressSection({ courseId }: { courseId: string }): JSX.Element | null {
  const meQuery = useQuery({ queryKey: ['wiki', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const canSeeTeam = meQuery.data?.type === 'crm_user' || (meQuery.data?.type === 'iiko_employee' && meQuery.data.roleCode === 'MANAGER');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['wiki', 'education', 'team-progress', courseId],
    queryFn: () => educationApi.getTeamProgress(courseId),
    enabled: canSeeTeam,
    retry: false,
  });
  if (!canSeeTeam || isError) return null;
  if (isLoading) return <Skeleton active paragraph={{ rows: 2 }} />;
  const rows = data ?? [];
  return (
    <section className="fk-learn-card fk-learn-pad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <TrophyOutlined style={{ color: 'var(--fk-orange)' }} />
        <h2 className="fk-learn-h2" style={{ margin: 0 }}>Прогресс команды</h2>
        <AntTag style={{ margin: 0 }}>{rows.length}</AntTag>
      </div>
      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Пока никто не начал курс" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--fk-text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '6px 8px' }}>Сотрудник</th>
                <th style={{ padding: '6px 8px' }}>Роль</th>
                <th style={{ padding: '6px 8px', width: 160 }}>Прогресс</th>
                <th style={{ padding: '6px 8px' }}>Лучший балл</th>
                <th style={{ padding: '6px 8px' }}>Завершён</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ownerKey} style={{ borderTop: '1px solid var(--fk-row-line)' }}>
                  <td style={{ padding: 8, fontWeight: 600 }}>
                    {r.fullName}
                    {r.kind === 'employee' && <AntTag style={{ marginLeft: 6 }}>iiko</AntTag>}
                  </td>
                  <td style={{ padding: 8, color: 'var(--fk-text-2)' }}>{r.roleName}</td>
                  <td style={{ padding: 8 }}>
                    <Progress percent={r.progressPercent} size="small" strokeColor={r.completedAt ? 'var(--fk-success)' : 'var(--fk-orange)'} />
                  </td>
                  <td style={{ padding: 8 }}>
                    {r.bestScore !== null ? <AntTag color={r.bestScore >= 70 ? 'green' : 'orange'} style={{ margin: 0 }}>{r.bestScore}%</AntTag> : <span style={{ color: 'var(--fk-text-muted)' }}>—</span>}
                  </td>
                  <td style={{ padding: 8, color: 'var(--fk-text-muted)' }}>
                    {r.completedAt ? new Date(r.completedAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** «Мои навыки» (фаза 3.1): уровни 0–3 по станциям роли. */
function MySkillsBlock(): JSX.Element | null {
  const q = useQuery({ queryKey: ['wiki', 'education', 'skills'], queryFn: () => educationApi.mySkills(), retry: false });
  const skills = q.data ?? [];
  if (skills.length === 0) return null;
  const tone = ['var(--fk-border)', 'var(--fk-info)', '#7E3FB8', 'var(--fk-success)'];
  return (
    <section className="fk-learn-card fk-learn-pad">
      <h2 className="fk-learn-h2">Мои навыки</h2>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {skills.map((s) => (
          <div key={s.id} style={{ border: '1px solid var(--fk-border)', borderRadius: 8, padding: '10px 12px', minWidth: 180, flex: '1 1 180px', maxWidth: 280 }}>
            <div style={{ fontWeight: 700 }}>{s.name}</div>
            <div style={{ display: 'flex', gap: 4, margin: '6px 0' }}>
              {[1, 2, 3].map((l) => <span key={l} style={{ width: 22, height: 8, borderRadius: 4, background: s.level >= l ? tone[l] : 'var(--fk-bg-subtle)', border: `1px solid ${s.level >= l ? tone[l] : 'var(--fk-border)'}` }} />)}
            </div>
            <div style={{ fontSize: 12, color: s.expired ? 'var(--fk-error)' : 'var(--fk-text-muted)' }}>
              {s.expired ? 'подтверждение истекло' : s.levelName}{s.expiresAt && !s.expired ? ` · до ${new Date(s.expiresAt).toLocaleDateString('ru-RU')}` : ''}
            </div>
            {s.level < 1 && s.courseId && <Link to={`/course/${s.courseId}`} style={{ fontSize: 12.5 }}>Пройти курс →</Link>}
          </div>
        ))}
      </div>
    </section>
  );
}
